// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../utils/axiosInstance";
import { socket, joinUserRoom } from "../socket";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem("role"));
  const [role, setRole] = useState(localStorage.getItem("role") || null);
  const [teamRole, setTeamRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, verify session by calling /api/auth/me (cookie is sent automatically)
  useEffect(() => {
    const verifySession = async () => {
      try {
        const res = await api.get("/api/auth/me");
        if (res.data?.success && res.data?.user) {
          setUser(res.data.user);
          setIsAuthenticated(true);
          const userRole = res.data.user.role || "user";
          setRole(userRole);
          setTeamRole(res.data.user.teamRole || null);
          localStorage.setItem("role", userRole);
          if (res.data.user.activeWorkspaceId) {
            localStorage.setItem("activeWorkspaceId", res.data.user.activeWorkspaceId);
          } else if (res.data.user.activeWorkspace?.id) {
            localStorage.setItem("activeWorkspaceId", res.data.user.activeWorkspace.id);
          }
          joinUserRoom(res.data.user.id);
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setRole(null);
          setTeamRole(null);
          localStorage.removeItem("role");
          localStorage.removeItem("activeWorkspaceId");
        }
      } catch {
        // Cookie expired or invalid — user is not authenticated
        setUser(null);
        setIsAuthenticated(false);
        setRole(null);
        setTeamRole(null);
        localStorage.removeItem("role");
        localStorage.removeItem("activeWorkspaceId");
      } finally {
        setLoading(false);
      }
    };
    verifySession();
  }, []);

  // Login: called after successful API login; role comes from response
  const login = useCallback((newRole) => {
    // Token is now in httpOnly cookie — no localStorage needed for token
    const effectiveRole = newRole || "user";
    setRole(effectiveRole);
    setIsAuthenticated(true);
    localStorage.setItem("role", effectiveRole);

    // Fetch full user info from /me
    api.get("/api/auth/me").then((res) => {
      if (res.data?.success && res.data?.user) {
        setUser(res.data.user);
        setTeamRole(res.data.user.teamRole || null);
        if (res.data.user.activeWorkspaceId) {
          localStorage.setItem("activeWorkspaceId", res.data.user.activeWorkspaceId);
        } else if (res.data.user.activeWorkspace?.id) {
          localStorage.setItem("activeWorkspaceId", res.data.user.activeWorkspace.id);
        }
        joinUserRoom(res.data.user.id);
      }
    }).catch(() => {});
  }, []);

  // Re-join user room on socket reconnect (room membership is lost on disconnect)
  useEffect(() => {
    const onReconnect = () => {
      if (user?.id) joinUserRoom(user.id);
    };
    socket.on("connect", onReconnect);
    return () => socket.off("connect", onReconnect);
  }, [user]);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      // Ignore logout API errors
    }
    localStorage.removeItem("role");
    localStorage.removeItem("activeWorkspaceId");
    setUser(null);
    setIsAuthenticated(false);
    setRole(null);
    setTeamRole(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, role, teamRole, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
